import React, { Suspense, lazy } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { PageProvider } from "./context/PageContext";
import { Toaster } from "./components/ui/sonner";
import { ConfirmProvider } from "./components/ConfirmProvider";
import { Loader2 } from "lucide-react";

// Lazy load pages
const LoginPage = lazy(() => import("./pages/LoginPage"));
const SetupPage = lazy(() => import("./pages/SetupPage"));
const PublicModulePage = lazy(() => import("./pages/PublicModulePage"));
const RegisterPage = lazy(() => import("./pages/RegisterPage"));
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const MenuBuilderPage = lazy(() => import("./pages/MenuBuilderPage"));
const ModuleBuilderPage = lazy(() => import("./pages/ModuleBuilderPage"));
const ModuleViewPage = lazy(() => import("./pages/ModuleViewPage"));
const RecordFormPage = lazy(() => import("./pages/RecordFormPage"));
const RolesPage = lazy(() => import("./pages/RolesPage"));
const UsersPage = lazy(() => import("./pages/UsersPage"));
const WorkflowsPage = lazy(() => import("./pages/WorkflowsPage"));
const EmailTemplatesPage = lazy(() => import("./pages/EmailTemplatesPage"));
const PrintTemplatesPage = lazy(() => import("./pages/PrintTemplatesPage"));
const BackgroundJobsPage = lazy(() => import("./pages/BackgroundJobsPage"));
const SmtpConfigPage = lazy(() => import("./pages/SmtpConfigPage"));
const AppSetupPage = lazy(() => import("./pages/AppSetupPage"));
const PreBuiltAppsPage = lazy(() => import("./pages/PreBuiltAppsPage"));
const AuditLogPage = lazy(() => import("./pages/AuditLogPage"));
const TokensPage = lazy(() => import("./pages/TokensPage"));
const SupportTicketsPage = lazy(() => import("./pages/SupportTicketsPage"));
const ConnectedModulesPage = lazy(() => import("./pages/ConnectedModulesPage"));
const AppsPage = lazy(() => import("./pages/AppsPage"));
const AIDataAssistantPage = lazy(() => import("./pages/AIDataAssistantPage"));
const ReportViewPage = lazy(() => import("./pages/ReportViewPage"));
const ReportsListPage = lazy(() => import("./pages/ReportsListPage"));
const ReportBuilderPage = lazy(() => import("./pages/ReportBuilderPage"));
const WorkflowsListPage = lazy(() => import("./pages/WorkflowsListPage"));
const WorkflowBuilderPage = lazy(() => import("./pages/WorkflowBuilderPage"));
const CustomPageView = lazy(() => import("./pages/CustomPageView"));
const MainLayout = lazy(() => import("./layouts/MainLayout"));

// Loading component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background" data-testid="page-loader">
    <div className="flex flex-col items-center gap-2">
      <Loader2 className="h-5 w-5 animate-spin text-primary" />
      <p className="text-xs text-muted-foreground">Loading...</p>
    </div>
  </div>
);

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <PageLoader />;
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Creator Only Route
const CreatorRoute = ({ children }) => {
  const { user, loading, isCreator } = useAuth();
  
  if (loading) {
    return <PageLoader />;
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (!isCreator) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Admin Route
const AdminRoute = ({ children }) => {
  const { user, loading, isAdmin } = useAuth();

  if (loading) {
    return <PageLoader />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Admin OR Creator — used for shared ops pages like AI Token Billing.
const AdminOrCreatorRoute = ({ children }) => {
  const { user, loading, isAdmin, isCreator } = useAuth();
  if (loading) return <PageLoader />;
  if (!user) return <Navigate to="/login" replace />;
  if (!isAdmin && !isCreator) return <Navigate to="/dashboard" replace />;
  return children;
};

// Public Route (redirect if logged in)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <PageLoader />;
  }
  
  if (user) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public Routes */}
        <Route path="/login" element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        } />
        <Route path="/register" element={
          <PublicRoute>
            <RegisterPage />
          </PublicRoute>
        } />

        {/* Public module form — anonymous, no layout, no auth. Tenant-scoped
            via the request's Origin/Host header on the backend. */}
        <Route path="/public/:slug" element={<PublicModulePage />} />

        {/* First-run tenant setup — public, shown once for a brand-new domain. */}
        <Route path="/setup" element={<SetupPage />} />
        
        {/* Protected Routes */}
        <Route path="/" element={
          <ProtectedRoute>
            <MainLayout />
          </ProtectedRoute>
        }>
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="dashboard" element={<DashboardPage />} />
          <Route path="module/:moduleId" element={<ModuleViewPage />} />
          <Route path="module/:moduleId/new" element={<RecordFormPage />} />
          <Route path="module/:moduleId/edit/:recordId" element={<RecordFormPage />} />
          <Route path="report/:reportId" element={<ReportViewPage />} />
          <Route path="report-view/:moduleId" element={<ReportViewPage />} />
          {/* Report Builder is a creator-only feature. End-users still get to
              VIEW an individual report via /report/:reportId (no guard below)
              — the LIST + builder pages are gated. */}
          <Route path="reports" element={<CreatorRoute><ReportsListPage /></CreatorRoute>} />
          <Route path="report-builder" element={<CreatorRoute><ReportBuilderPage /></CreatorRoute>} />
          <Route path="report-builder/:id" element={<CreatorRoute><ReportBuilderPage /></CreatorRoute>} />
          <Route path="workflows" element={<CreatorRoute><WorkflowsListPage /></CreatorRoute>} />
          <Route path="workflow-builder" element={<CreatorRoute><WorkflowBuilderPage /></CreatorRoute>} />
          <Route path="workflow-builder/:id" element={<CreatorRoute><WorkflowBuilderPage /></CreatorRoute>} />
          <Route path="page/:id" element={<CustomPageView />} />
          
          {/* Admin Routes */}
          <Route path="roles" element={
            <AdminRoute>
              <RolesPage />
            </AdminRoute>
          } />
          <Route path="users" element={
            <AdminRoute>
              <UsersPage />
            </AdminRoute>
          } />
          <Route path="support-tickets" element={
            <AdminOrCreatorRoute>
              <SupportTicketsPage />
            </AdminOrCreatorRoute>
          } />
          <Route path="tokens" element={
            <AdminOrCreatorRoute>
              <TokensPage />
            </AdminOrCreatorRoute>
          } />
          <Route path="apps" element={
            <CreatorRoute>
              <AppsPage />
            </CreatorRoute>
          } />
          <Route path="workflows" element={
            <AdminRoute>
              <WorkflowsPage />
            </AdminRoute>
          } />
          <Route path="email-templates" element={
            <AdminRoute>
              <EmailTemplatesPage />
            </AdminRoute>
          } />
          <Route path="print-templates" element={
            <AdminRoute>
              <PrintTemplatesPage />
            </AdminRoute>
          } />
          <Route path="background-jobs" element={
            <AdminRoute>
              <BackgroundJobsPage />
            </AdminRoute>
          } />
          <Route path="smtp-config" element={
            <AdminRoute>
              <SmtpConfigPage />
            </AdminRoute>
          } />
          
          {/* Creator Routes */}
          <Route path="menu-builder" element={
            <CreatorRoute>
              <MenuBuilderPage />
            </CreatorRoute>
          } />
          <Route path="module-builder" element={
            <CreatorRoute>
              <ModuleBuilderPage />
            </CreatorRoute>
          } />
          <Route path="app-setup" element={
            <CreatorRoute>
              <AppSetupPage />
            </CreatorRoute>
          } />
          <Route path="pre-built-apps" element={
            <CreatorRoute>
              <PreBuiltAppsPage />
            </CreatorRoute>
          } />
          <Route path="audit-log" element={
            <CreatorRoute>
              <AuditLogPage />
            </CreatorRoute>
          } />
          <Route path="connected-modules" element={
            <CreatorRoute>
              <ConnectedModulesPage />
            </CreatorRoute>
          } />
          <Route path="ai-assistant" element={<AIDataAssistantPage />} />
        </Route>
        
        {/* Catch all */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <PageProvider>
            <ConfirmProvider>
              <AppRoutes />
              <Toaster position="top-right" richColors />
            </ConfirmProvider>
          </PageProvider>
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
